/**
 * @file index.tsx
 *
 * @fileoverview Renders a file upload input.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React, { useEffect, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { Controller, useFormContext } from 'react-hook-form';
import { getSetting } from '~/jobiqo/components/Form/helpers/formSettings';
import { FileInput } from '~/vcl/atoms/01-forms/07-file/02-file';
import InlineError from '~/vcl/atoms/01-forms/09-inline-error';
import { getCommonTranslations } from '~/jobiqo/utils/intl';
import { useFileInputDefaultFiles } from '~/jobiqo/components/Form/hooks/useFileInputDefaultFiles';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../hooks/useFieldRules';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

export const getFileTypeCorrectNames = (types: string[]): string[] => {
  return types.map((type) => {
    return `.${type}`;
  });
};

/**
 * Constructs Yup validation for multiple files component.
 * It adds files' sum size validation and a maximum number
 * of uploaded files validation.
 *
 * @param Yup
 * @param {IntlShape} intl
 * @param {string} maxUploadSize
 * The maximum allowed upload size and MB unit.
 * e.g "5 MB"
 * @param filesAmountLimit
 * An optional limit for number of files to upload.
 * @param {boolean} required
 * If the field is required.
 * @returns
 * Yup validation object.
 */
export function getYupMultipleFilesValidation(
  Yup,
  intl: IntlShape,
  maxUploadSize = '',
  filesAmountLimit: number | null = null,
  required = false,
) {
  let yupValidation = Yup.mixed();

  if (required) {
    try {
      yupValidation = yupValidation.test(
        'required',
        getCommonTranslations().FIELD_REQUIRED,
        (value) => {
          if (value?.length === 0 || value == null) {
            return false;
          }
          return true;
        },
      );
    } catch (err) {}
  }

  if (maxUploadSize) {
    try {
      yupValidation = yupValidation.test(
        'fileSize',
        intl.formatMessage(
          {
            id: 'files_upload_size_exceeded_msg',
            defaultMessage: 'Maximum upload size is {maxUploadSize}',
          },
          { maxUploadSize },
        ),
        (value) => {
          // Try to extract size in MB from argument maxUploadSize.
          const sizeLimit =
            Number(maxUploadSize.split(' ').shift()) * 1024 * 1024;
          if (value) {
            const filesSize = (Array.isArray(value) ? value : [value]).reduce(
              (total: number, file: File) => total + file.size,
              0,
            );

            if (filesSize > sizeLimit) {
              return false;
            }
          }
          return true;
        },
      );
    } catch (err) {
      console.error(
        `Error while extracting MB size from argument: ${maxUploadSize}`,
        err,
      );
    }
  }

  if (filesAmountLimit && filesAmountLimit > 0) {
    yupValidation = yupValidation.test(
      'filesNumber',
      intl.formatMessage(
        {
          id: 'multiple_files_upload_files_number_exceeded_msg',
          defaultMessage:
            'Too many files selected: Attach up to {filesAmountLimit} files',
        },
        { filesAmountLimit },
      ),
      (value) => {
        if (!value || !Array.isArray(value)) {
          return true;
        } else {
          return value.length <= filesAmountLimit;
        }
      },
    );
  }

  return yupValidation;
}

const FileWidget = ({
  entityField,
  fieldRules = {},
  value,
  ...props
}: Props) => {
  const intl = useIntl();
  const {
    control,
    setValue,
    getValues,
    formState: { isSubmitted, touchedFields },
  } = useFormContext();
  const [newFileAdded, setNewFileAdded] = React.useState(false);

  const { defaultValues, handleRemoveExistingFile } = useFileInputDefaultFiles(
    Array.isArray(value)
      ? (value
          ?.map((attachment) => attachment?.url)
          .filter(Boolean) as string[])
      : value,
    entityField.multiple,
  );
  const values = getValues();
  const { hidden, disabled, required } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );

  const fileTypes = useMemo(() => getSetting('file_extensions', entityField), [
    entityField.settings,
  ]);

  // Only trigger validation when field was edited or form was submitted.
  const shouldValidate = isSubmitted || touchedFields[entityField.id];

  function updateValue(value) {
    setValue(entityField.id, value, {
      shouldValidate: true,
    });
  }

  useEffect(() => {
    const fieldValues = values[entityField.id];

    let elValue = fieldValues ?? [];
    if (Array.isArray(elValue)) {
      // Remove all default values from value first - because default values
      // might be less than the current value
      const newValues = elValue.filter(
        (file) =>
          // Not a file and
          (file.name || file.url) &&
          !defaultValues.some((attachment) =>
            file.url ? attachment === file.url : attachment === file,
          ),
      );

      // Add default values to value
      const uniqueValues = Array.from(
        new Set([...newValues, ...defaultValues]),
      );

      updateValue(uniqueValues);
      setNewFileAdded(false);
    }
    // Only update value if it's not already set by FileInput component.
    else if (!(fieldValues instanceof File)) {
      updateValue(defaultValues[0]);
    }
  }, [defaultValues, entityField.id, newFileAdded]);

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField]);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      name={entityField.id}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <FileInput
            {...field}
            defaultFiles={defaultValues}
            id={field.name}
            type={entityField.type}
            required={entityField.required || required}
            disabled={entityField.disabled || disabled}
            error={!!error && shouldValidate}
            label={entityField.label}
            defaultViewFileLabel={intl.formatMessage({
              id: 'view_file_btn_text',
              defaultMessage: 'View file',
            })}
            multiple={entityField.multiple}
            placeholder={intl.formatMessage({
              id: 'choose_file_btn_text',
              defaultMessage: 'Upload',
            })}
            description={entityField.description}
            {...(fileTypes
              ? { fileTypes: getFileTypeCorrectNames(fileTypes.split(' ')) }
              : null)}
            {...props}
            onAdd={(files) => {
              setNewFileAdded(true);
              if (entityField.multiple) {
                updateValue([...(field.value ?? []), ...files]);
              } else {
                handleRemoveExistingFile(0);
                updateValue(files[0]);
              }
            }}
            onRemove={(file) => {
              if (entityField.multiple) {
                updateValue(
                  field.value.filter((_file) => _file.name !== file.name),
                );
              } else {
                updateValue(null);
              }
            }}
            onRemoveExistingFile={handleRemoveExistingFile}
          />
          {!!error && shouldValidate && <InlineError message={error.message} />}
        </>
      )}
    />
  );
};

export default FileWidget;
